import { useRef, type ReactNode } from 'react';
import {
  Button,
  cn,
  FormField,
  FormFieldError,
  FormFieldHint,
  FormFieldLabel,
  Progress,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@eureca/ui';
import { LockIcon } from '@eureca/ui/icons';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { JobHeaderLogo } from '@/features/jobs/job-shared/JobHeaderLogo';
import type { BulkPhaseOption } from './bulk-flow';

/**
 * EU-551/EU-552 — wizard de 2 etapas compartilhado (Movimentar/Reprovar
 * candidatos): Etapa 1 "Base[, origem e destino]" (Programa/Vaga bloqueados +
 * upload XLSX + selects de fase) e Etapa 2 "Confirmação" (Resumo + Conflitos
 * condicionais + confirmação com loading). O container decide o variant.
 */

export type BulkWizardVariant = 'move' | 'reject';

/** Ordem canônica dos passos — base do progresso proporcional do header. */
const WIZARD_STEPS: readonly ('form' | 'confirm')[] = ['form', 'confirm'];

export interface BulkWizardSummary {
  /** Quantidade de candidatos aptos (que serão processados). */
  candidateCount: number;
  originName: string | null;
  /** Só no variant `move`. */
  destName?: string | null;
  /** Presente apenas quando o destino é Jornada de aprofundamento (RN-05). */
  fatorH?: boolean;
}

export interface BulkWizardConflictCounts {
  cancelled: number;
  alreadyProcessed: number;
  /** Reprovados — decisão terminal, não movimentam (só move; 2026-07-30). */
  rejected: number;
  /** MOV-INC-05 — aptos fora da "Fase de origem" selecionada (só move). */
  notInOrigin: number;
  invalid: number;
}

export interface BulkCandidatesWizardProps {
  variant: BulkWizardVariant;
  title: string;
  companyLogoUrl?: string | null;
  companyName?: string | null;
  programName: string;
  jobName: string;
  phases: BulkPhaseOption[];

  step: 'form' | 'confirm';

  // ── Etapa 1 ──
  fileName: string | null;
  fileError: string | null;
  /** RN-08/RN-04 — "Base sem candidatos aptos..." (pausa o avanço). */
  baseError: string | null;
  originStepId: string;
  destStepId?: string;
  /** Erro de validação do destino (ex.: origem = destino). */
  destError?: string | null;
  /** RN-05 — visível só com destino = Jornada de aprofundamento. */
  fatorHVisible?: boolean;
  fatorHChecked?: boolean;
  /** QA 2026-07-30 — disparo automático ainda não testável: checkbox travado. */
  fatorHDisabled?: boolean;
  canAdvance: boolean;
  /** Tooltip do Avançar desabilitado ("Todos os campos são obrigatórios" / "Preencha os campos corretamente"). */
  advanceTooltip: string | null;
  isProcessing: boolean;

  // ── Etapa 2 ──
  summary: BulkWizardSummary | null;
  conflicts: BulkWizardConflictCounts | null;
  confirmLabel: string;

  onFileSelect: (file: File | null) => void;
  onDownloadTemplate: () => void;
  onOriginChange: (stepId: string) => void;
  onDestChange?: (stepId: string) => void;
  onFatorHChange?: (checked: boolean) => void;
  onBack: () => void;
  onAdvance: () => void;
  onDownloadConflicts: () => void;
  onConfirm: () => void;
}

const SELECT_CLASS = cn(
  'h-9 w-full appearance-none rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-2xs',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary',
  'disabled:cursor-not-allowed disabled:opacity-70'
);

export function BulkCandidatesWizard({
  variant,
  title,
  companyLogoUrl,
  companyName,
  programName,
  jobName,
  phases,
  step,
  fileName,
  fileError,
  baseError,
  originStepId,
  destStepId = '',
  destError = null,
  fatorHVisible = false,
  fatorHChecked = false,
  fatorHDisabled = false,
  canAdvance,
  advanceTooltip,
  isProcessing,
  summary,
  conflicts,
  confirmLabel,
  onFileSelect,
  onDownloadTemplate,
  onOriginChange,
  onDestChange,
  onFatorHChange,
  onBack,
  onAdvance,
  onDownloadConflicts,
  onConfirm,
}: BulkCandidatesWizardProps) {
  const testIdPrefix = variant === 'move' ? 'job-move' : 'job-reject';
  const isConfirm = step === 'confirm';
  // Progresso proporcional à posição no wizard — derivado da lista de passos
  // (um passo novo entra em WIZARD_STEPS e a barra se ajusta sozinha).
  const progress = ((WIZARD_STEPS.indexOf(step) + 1) / WIZARD_STEPS.length) * 100;
  const subtitle = isConfirm
    ? 'Confirmação'
    : variant === 'move'
      ? 'Base, origem e destino'
      : 'Base e origem';
  const hasConflicts =
    conflicts !== null &&
    conflicts.cancelled +
      conflicts.alreadyProcessed +
      conflicts.rejected +
      conflicts.notInOrigin +
      conflicts.invalid >
      0;

  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="jobs" />

      <main className="flex flex-1 flex-col items-center bg-card px-4 pt-8 pb-16 md:px-8">
        <div className="flex min-h-full w-full max-w-[630px] flex-col gap-8">
          <header className="flex flex-col gap-2">
            <div className="flex flex-row items-center gap-3">
              <JobHeaderLogo logoUrl={companyLogoUrl} companyName={companyName} />
              <h1 className="text-3xl font-medium leading-none tracking-tight text-foreground md:text-4xl">
                {title}
              </h1>
            </div>
            <p className="text-lg text-muted-foreground">{subtitle}</p>
            <Progress value={progress} className="mt-2 h-2 bg-secondary/40" />
          </header>

          {isConfirm && summary ? (
            <ConfirmStep
              testIdPrefix={testIdPrefix}
              variant={variant}
              programName={programName}
              jobName={jobName}
              summary={summary}
              conflicts={conflicts}
            />
          ) : (
            <FormStep
              testIdPrefix={testIdPrefix}
              variant={variant}
              programName={programName}
              jobName={jobName}
              phases={phases}
              fileName={fileName}
              fileError={fileError}
              baseError={baseError}
              originStepId={originStepId}
              destStepId={destStepId}
              destError={destError}
              fatorHVisible={fatorHVisible}
              fatorHChecked={fatorHChecked}
              fatorHDisabled={fatorHDisabled}
              onFileSelect={onFileSelect}
              onDownloadTemplate={onDownloadTemplate}
              onOriginChange={onOriginChange}
              onDestChange={onDestChange}
              onFatorHChange={onFatorHChange}
            />
          )}

          <footer className="mt-auto flex flex-row items-center justify-between gap-3 pt-4">
            <Button
              variant="outline"
              size="lg"
              data-testid={`${testIdPrefix}-back`}
              onClick={onBack}
              disabled={isProcessing}
            >
              Voltar
            </Button>
            {isConfirm ? (
              <div className="flex flex-row items-center gap-3">
                {hasConflicts ? (
                  <Button
                    variant="secondary"
                    size="lg"
                    data-testid={`${testIdPrefix}-download-conflicts`}
                    onClick={onDownloadConflicts}
                    disabled={isProcessing}
                  >
                    Baixar base de conflitos
                  </Button>
                ) : null}
                <Button
                  size="lg"
                  data-testid={`${testIdPrefix}-confirm`}
                  onClick={onConfirm}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <span className="inline-flex items-center gap-2">
                      <Spinner className="size-4" /> Processando...
                    </span>
                  ) : (
                    confirmLabel
                  )}
                </Button>
              </div>
            ) : (
              <AdvanceButton
                testIdPrefix={testIdPrefix}
                canAdvance={canAdvance && !isProcessing}
                tooltip={advanceTooltip}
                isProcessing={isProcessing}
                onAdvance={onAdvance}
              />
            )}
          </footer>
        </div>
      </main>
    </div>
  );
}

/**
 * "Avançar" desabilitado com tooltip (validações do card). Radix Tooltip não
 * dispara em botão `disabled` — wrapper focável, mesmo padrão da FormsListPage.
 */
function AdvanceButton({
  testIdPrefix,
  canAdvance,
  tooltip,
  isProcessing,
  onAdvance,
}: {
  testIdPrefix: string;
  canAdvance: boolean;
  tooltip: string | null;
  isProcessing: boolean;
  onAdvance: () => void;
}) {
  if (canAdvance) {
    return (
      <Button size="lg" data-testid={`${testIdPrefix}-advance`} onClick={onAdvance}>
        Avançar
      </Button>
    );
  }
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} data-testid={`${testIdPrefix}-advance-disabled-wrapper`}>
            <Button size="lg" data-testid={`${testIdPrefix}-advance`} disabled>
              {isProcessing ? (
                <span className="inline-flex items-center gap-2">
                  <Spinner className="size-4" /> Processando...
                </span>
              ) : (
                'Avançar'
              )}
            </Button>
          </span>
        </TooltipTrigger>
        {tooltip ? <TooltipContent>{tooltip}</TooltipContent> : null}
      </Tooltip>
    </TooltipProvider>
  );
}

function FormStep({
  testIdPrefix,
  variant,
  programName,
  jobName,
  phases,
  fileName,
  fileError,
  baseError,
  originStepId,
  destStepId,
  destError,
  fatorHVisible,
  fatorHChecked,
  fatorHDisabled,
  onFileSelect,
  onDownloadTemplate,
  onOriginChange,
  onDestChange,
  onFatorHChange,
}: {
  testIdPrefix: string;
  variant: BulkWizardVariant;
  programName: string;
  jobName: string;
  phases: BulkPhaseOption[];
  fileName: string | null;
  fileError: string | null;
  baseError: string | null;
  originStepId: string;
  destStepId: string;
  destError: string | null;
  fatorHVisible: boolean;
  fatorHChecked: boolean;
  fatorHDisabled: boolean;
  onFileSelect: (file: File | null) => void;
  onDownloadTemplate: () => void;
  onOriginChange: (stepId: string) => void;
  onDestChange?: (stepId: string) => void;
  onFatorHChange?: (checked: boolean) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadError = fileError ?? baseError;

  return (
    <div className="flex flex-col gap-6">
      {/* MOV-001/REP-001 — Programa e Vaga preenchidos e bloqueados; o cadeado
          (Figma, QA 2026-07-29) sinaliza visualmente o bloqueio herdado. */}
      <FormField>
        <FormFieldLabel>Programa</FormFieldLabel>
        <div className="relative">
          <input
            data-testid={`${testIdPrefix}-program`}
            className={cn(SELECT_CLASS, 'pr-9')}
            value={programName}
            disabled
            readOnly
          />
          <LockIcon
            aria-hidden
            data-testid={`${testIdPrefix}-program-lock`}
            className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
        </div>
      </FormField>
      <FormField>
        <FormFieldLabel>Vaga</FormFieldLabel>
        <div className="relative">
          <input
            data-testid={`${testIdPrefix}-job`}
            className={cn(SELECT_CLASS, 'pr-9')}
            value={jobName}
            disabled
            readOnly
          />
          <LockIcon
            aria-hidden
            data-testid={`${testIdPrefix}-job-lock`}
            className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
        </div>
      </FormField>

      <FormField error={Boolean(uploadError)}>
        <FormFieldLabel>Base de candidatos</FormFieldLabel>
        <button
          type="button"
          data-testid={`${testIdPrefix}-file`}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            'flex h-9 w-full flex-row items-center rounded-md border bg-background px-3 text-left text-sm shadow-2xs',
            uploadError ? 'border-destructive' : 'border-input',
            fileName ? 'text-foreground' : 'text-muted-foreground'
          )}
        >
          {fileName ?? 'Clique para selecionar um arquivo'}
        </button>
        <input
          ref={fileInputRef}
          data-testid={`${testIdPrefix}-file-input`}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(e) => {
            onFileSelect(e.target.files?.[0] ?? null);
            // Permite re-selecionar o MESMO arquivo após corrigir a planilha.
            e.target.value = '';
          }}
        />
        {uploadError ? (
          <FormFieldError data-testid={`${testIdPrefix}-file-error`}>{uploadError}</FormFieldError>
        ) : (
          <FormFieldHint>
            Envie em .XLSX, com no máximo 5 MB.{' '}
            <button
              type="button"
              data-testid={`${testIdPrefix}-download-template`}
              onClick={onDownloadTemplate}
              className="font-medium text-primary underline underline-offset-2"
            >
              Baixar modelo
            </button>
          </FormFieldHint>
        )}
      </FormField>

      <FormField>
        <FormFieldLabel>Fase de origem</FormFieldLabel>
        <select
          data-testid={`${testIdPrefix}-origin`}
          className={SELECT_CLASS}
          value={originStepId}
          onChange={(e) => onOriginChange(e.target.value)}
        >
          <option value="" disabled>
            Selecione uma fase
          </option>
          {phases.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </FormField>

      {variant === 'move' ? (
        <FormField error={Boolean(destError)}>
          <FormFieldLabel>Fase de destino</FormFieldLabel>
          <select
            data-testid={`${testIdPrefix}-dest`}
            className={SELECT_CLASS}
            value={destStepId}
            onChange={(e) => onDestChange?.(e.target.value)}
          >
            <option value="" disabled>
              Selecione uma fase
            </option>
            {phases.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {destError ? (
            <FormFieldError data-testid={`${testIdPrefix}-dest-error`}>{destError}</FormFieldError>
          ) : null}
        </FormField>
      ) : null}

      {fatorHVisible ? (
        <label
          className={cn(
            'flex flex-row items-center gap-2 text-sm text-foreground',
            fatorHDisabled && 'cursor-not-allowed opacity-60'
          )}
          data-testid={`${testIdPrefix}-fatorh-wrapper`}
        >
          <input
            data-testid={`${testIdPrefix}-fatorh`}
            type="checkbox"
            checked={fatorHChecked}
            disabled={fatorHDisabled}
            onChange={(e) => onFatorHChange?.(e.target.checked)}
            className="size-4 accent-primary"
          />
          Disparar Fator H imediatamente
          {fatorHDisabled ? (
            <span className="text-xs text-muted-foreground">(indisponível no momento)</span>
          ) : null}
        </label>
      ) : null}
    </div>
  );
}

function SummaryRow({
  label,
  value,
  testId,
}: {
  label: string;
  value: ReactNode;
  testId?: string;
}) {
  return (
    <div className="flex flex-row items-baseline justify-between gap-4" data-testid={testId}>
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground">{value}</span>
    </div>
  );
}

function ConfirmStep({
  testIdPrefix,
  variant,
  programName,
  jobName,
  summary,
  conflicts,
}: {
  testIdPrefix: string;
  variant: BulkWizardVariant;
  programName: string;
  jobName: string;
  summary: BulkWizardSummary;
  conflicts: BulkWizardConflictCounts | null;
}) {
  const hasConflicts =
    conflicts !== null &&
    conflicts.cancelled +
      conflicts.alreadyProcessed +
      conflicts.rejected +
      conflicts.notInOrigin +
      conflicts.invalid >
      0;

  return (
    <div className="flex flex-col gap-8" data-testid={`${testIdPrefix}-confirm-step`}>
      <section className="flex flex-col gap-3">
        <h2 className="text-xl font-medium text-foreground">Resumo</h2>
        <SummaryRow label="Programa" value={programName} />
        <SummaryRow label="Vaga" value={jobName} />
        <SummaryRow
          label="Quantidade de candidatos"
          value={summary.candidateCount.toLocaleString('pt-BR')}
          testId={`${testIdPrefix}-summary-count`}
        />
        {summary.originName ? (
          <SummaryRow label="Fase de origem" value={summary.originName} />
        ) : null}
        {variant === 'move' && summary.destName ? (
          <SummaryRow label="Fase de destino" value={summary.destName} />
        ) : null}
        {variant === 'move' && summary.fatorH !== undefined ? (
          // Só exibido quando o destino é Jornada de aprofundamento (RN da Etapa 2).
          <SummaryRow
            label="Disparar Fator H imediatamente"
            value={summary.fatorH ? 'Sim' : 'Não'}
            testId={`${testIdPrefix}-summary-fatorh`}
          />
        ) : null}
      </section>

      {hasConflicts && conflicts ? (
        <section className="flex flex-col gap-3" data-testid={`${testIdPrefix}-conflicts`}>
          <h2 className="text-xl font-medium text-foreground">Conflitos</h2>
          {conflicts.cancelled > 0 ? (
            <SummaryRow
              label="Desistiram da vaga"
              value={conflicts.cancelled.toLocaleString('pt-BR')}
              testId={`${testIdPrefix}-conflicts-cancelled`}
            />
          ) : null}
          {conflicts.alreadyProcessed > 0 ? (
            <SummaryRow
              label={variant === 'move' ? 'Já movimentados' : 'Já reprovados'}
              value={conflicts.alreadyProcessed.toLocaleString('pt-BR')}
              testId={`${testIdPrefix}-conflicts-already`}
            />
          ) : null}
          {conflicts.rejected > 0 ? (
            <SummaryRow
              label="Reprovados"
              value={conflicts.rejected.toLocaleString('pt-BR')}
              testId={`${testIdPrefix}-conflicts-rejected`}
            />
          ) : null}
          {conflicts.notInOrigin > 0 ? (
            <SummaryRow
              label="Fora da fase de origem"
              value={conflicts.notInOrigin.toLocaleString('pt-BR')}
              testId={`${testIdPrefix}-conflicts-not-in-origin`}
            />
          ) : null}
          {conflicts.invalid > 0 ? (
            <SummaryRow
              label="IDs inválidos"
              value={conflicts.invalid.toLocaleString('pt-BR')}
              testId={`${testIdPrefix}-conflicts-invalid`}
            />
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
